import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <div className="bg-card rounded-lg border border-border p-8 shadow-sm">
          <h1 className="text-4xl font-heading font-extrabold text-foreground mb-6">Privacy Policy</h1>
          
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-6">
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="font-semibold text-foreground">Controller:</p>
              <p>2DS Finland Oy</p>
              <p>Business ID: 3246359-5</p>
              <p>Espoo, Finland</p>
            </div>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">1. Scope</h2>
              <p>This Privacy Policy explains how we process personal data when you use the Service.</p>
              <p className="mt-2">We act as:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Data Controller for account and billing data</li>
                <li>Data Processor for customer data you upload</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">2. Data We Collect</h2>
              
              <h3 className="font-semibold text-foreground mt-4 mb-2">Account Data</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Name</li>
                <li>Email address</li>
                <li>Company name</li>
                <li>Login credentials (encrypted)</li>
              </ul>

              <h3 className="font-semibold text-foreground mt-4 mb-2">Usage Data</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Logins</li>
                <li>IP address</li>
                <li>Device/browser info</li>
                <li>Activity logs</li>
              </ul>

              <h3 className="font-semibold text-foreground mt-4 mb-2">Uploaded Data</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Customer contact details</li>
                <li>Booking information</li>
                <li>Invoices and receipts</li>
              </ul>

              <p className="mt-4 italic">We do not knowingly collect data from consumers or minors.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">3. Legal Basis (GDPR Article 6)</h2>
              <p>We process data based on:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Contractual necessity</li>
                <li>Legitimate interest</li>
                <li>Legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">4. Purpose of Processing</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide and operate the Service</li>
                <li>Billing and subscription management</li>
                <li>Security and fraud prevention</li>
                <li>Legal compliance</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">5. Data Storage & Transfers</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Data is stored in the EU</li>
                <li>Sub-processors may include: Hosting providers, Email services, Payment processors</li>
              </ul>
              <p className="mt-2">If data is transferred outside the EU, appropriate safeguards are applied.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">6. Data Retention</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Account data: retained while account is active</li>
                <li>Billing data: retained as required by law</li>
                <li>User Content: deleted upon request or account termination (subject to legal retention)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">7. Your Rights</h2>
              <p>Under GDPR, you have the right to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Access</li>
                <li>Rectification</li>
                <li>Erasure</li>
                <li>Restriction</li>
                <li>Data portability</li>
                <li>Objection</li>
              </ul>
              <p className="mt-2">Requests can be sent to: sales@2ds.fi</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">8. Security Measures</h2>
              <p>We implement:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Encryption</li>
                <li>Access controls</li>
                <li>Role-based permissions</li>
                <li>Audit logs</li>
              </ul>
              <p className="mt-2 italic">However, no system is 100% secure.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">9. Supervisory Authority</h2>
              <p>You may lodge a complaint with the Finnish Data Protection Ombudsman.</p>
            </section>

            <section className="border-t border-border pt-6 mt-6">
              <h2 className="text-xl font-semibold text-foreground mb-3">Cookies Policy</h2>
              <p>We use cookies strictly necessary for:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Authentication</li>
                <li>Session management</li>
                <li>Security</li>
              </ul>
              <p className="mt-4">We do not use:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Advertising cookies</li>
                <li>Third-party tracking cookies</li>
              </ul>
              <p className="mt-2 italic">Optional analytics cookies may be added later with consent.</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
